import type { Color, SceneElement, TextElement } from './types.js';

/**
 * The map element's contract, shared by everything that touches a map.
 *
 * It lives here rather than in the renderer because five different places read
 * the same three numbers — the renderer, the API's resolver, the widget's live
 * preview, the studio's editor and the importer — and until now each carried its
 * own idea of what a map with no answer should show. There were THREE different
 * Belgrade defaults and two different zoom clamps in the tree, which meant the
 * editor framed a map one way, the preview another and the print file a third.
 * One default, one clamp, one place.
 *
 * The numbers themselves are Customily's, read off the element its editor's
 * "Map" button creates: a world view at lat 17.9, lng -37.34, zoom 1, in Streets.
 * A merchant who drops a map and saves without touching it gets the same picture
 * in both products, which is the whole point of the parity rule.
 */

/** A pin drawn at the centre. Customily draws none; `null` means "none". */
export interface MapMarker {
  color?: Color;
  shape?: 'pin' | 'heart';
}

export interface MapConfig {
  lat?: number;
  lng?: number;
  /** Fractional — Customily stores things like 14.87 and rounding reframes the map. */
  zoom?: number;
  /** A key or label from the style catalogue, e.g. `noir`, `Monochrome - White`. */
  style?: string;
  /** The exact style name the template was migrated with, kept for the record. */
  sourceStyle?: string;
  /**
   * The looks the shopper may switch between, as LABELS, in the order the picker
   * shows them. Labels rather than keys because that is what a migrated swatch
   * value carries and what the shopper picked; the catalogue resolves either.
   */
  availableStyles?: string[];
  /**
   * `null` means "no pin", which is what Customily draws and therefore what a
   * migrated map must keep. `undefined` is "the default pin" — a heart — so the
   * two are genuinely different values and neither may be normalised away.
   */
  marker?: MapMarker | null;
  /**
   * How much ground `zoom` frames, independent of the layer's size: a square of
   * this many map pixels (256-pixel-tile pixels, the renderer's unit), scaled to
   * COVER the layer's box. Customily draws every map as a 500 × 500 static image
   * fitted over its placeholder, so a 280-unit mockup map and a 7000-unit print
   * map show the same streets — {@link CUSTOMILY_MAP_VIEWPORT} gives those
   * semantics.
   *
   * Absent, one scene unit is one map pixel at `zoom`, which is what every map
   * made before this field existed was framed with; it is not normalised away.
   */
  viewport?: number;
  /** The untranslated Customily config, kept for audit. */
  customily?: unknown;
}

/**
 * Customily's map frame: its storefront and print renderer both ask Mapbox for
 * `static/{lng},{lat},{zoom}/500x500@2x` and cover the placeholder with it, and
 * its location dialog is a 500 × 500 Mapbox GL map at the same zoom.
 *
 * 250, not 500: Mapbox GL and the Static Images API count zoom in 512-pixel
 * tiles, the raster tiles this renderer and its zoom numbers use are 256, so
 * Customily's 500 pixels at zoom z are 250 of ours at the same z. Measured
 * against the Static Images API itself (Kilometrima poster, 2026-09-25).
 */
export const CUSTOMILY_MAP_VIEWPORT = 250;

/** A usable viewport from a config, or undefined for the legacy per-unit framing. */
export function mapViewportOf(config: unknown): number | undefined {
  const viewport = (config as MapConfig | undefined)?.viewport;
  return typeof viewport === 'number' && Number.isFinite(viewport) && viewport > 0
    ? viewport
    : undefined;
}

/**
 * Zoom levels to add to a map's `zoom` to draw it in a box of `w` × `h` units
 * (or CSS pixels), at one unit per map pixel.
 *
 * With a viewport the framed square covers the box, so its LONGER side spans
 * `viewport` map pixels; without one the offset is zero. Pure and shared, so the
 * print renderer, the preview pan and the location dialogs agree to the pixel.
 */
export function mapZoomOffset(viewport: number | undefined, w: number, h: number): number {
  if (!viewport) return 0;
  const longer = Math.max(w, h);
  if (!Number.isFinite(longer) || longer <= 0) return 0;
  return Math.log2(longer / viewport);
}

/**
 * What Customily's "Map" button produces: a world view, in Streets.
 *
 * Not a placeholder — this is the preset, and matching it is the parity rule.
 */
export const MAP_DEFAULTS = {
  lat: 17.9,
  lng: -37.34,
  zoom: 1,
  style: 'Streets',
} as const;

/** The zoom levels a map may sit at, everywhere. */
export const MIN_MAP_ZOOM = 1;
export const MAX_MAP_ZOOM = 19;

/**
 * Holds a zoom inside the levels Mapbox actually serves.
 *
 * Fractional on purpose: the tiles come from a whole level and the grid is
 * magnified by the remainder, so 14.87 is a real, drawable framing.
 */
export function clampMapZoom(zoom: unknown, fallback: number = MAP_DEFAULTS.zoom): number {
  // `null` is not zero here: `Number(null)` is 0, which would clamp to 1 and
  // frame an unanswered map on the whole world instead of on the author's town.
  if (zoom == null || zoom === '') return fallback;
  const value = typeof zoom === 'number' ? zoom : Number(zoom);
  if (!Number.isFinite(value)) return fallback;
  return Math.min(MAX_MAP_ZOOM, Math.max(MIN_MAP_ZOOM, value));
}

/**
 * Where a map element is actually looking: the shopper's answer, else the
 * author's setting, else the preset.
 *
 * The shopper's `value` wins over `config` for the same reason a photo's crop
 * beats the template's — they framed it themselves, on the product page, and the
 * print file has to show what they saw.
 */
export function mapLocationOf(element: {
  config?: unknown;
  value?: unknown;
}): { lat: number; lng: number; zoom: number } {
  const config = (element.config ?? {}) as MapConfig;
  const value = (element.value ?? {}) as { lat?: unknown; lng?: unknown; zoom?: unknown };
  const pick = (a: unknown, b: unknown, fallback: number): number => {
    const first = a == null ? Number.NaN : Number(a);
    if (Number.isFinite(first)) return first;
    const second = b == null ? Number.NaN : Number(b);
    return Number.isFinite(second) ? second : fallback;
  };
  return {
    lat: pick(value.lat, config.lat, MAP_DEFAULTS.lat),
    lng: pick(value.lng, config.lng, MAP_DEFAULTS.lng),
    zoom: clampMapZoom(
      value.zoom ?? config.zoom ?? MAP_DEFAULTS.zoom,
      MAP_DEFAULTS.zoom,
    ),
  };
}

/** How a text layer linked to a map prints its coordinates. */
export type CoordinateFormat = 'decimal' | 'dms';

function dms(value: number, positive: string, negative: string): string {
  const hemisphere = value < 0 ? negative : positive;
  const abs = Math.abs(value);
  let degrees = Math.floor(abs);
  let minutes = Math.floor((abs - degrees) * 60);
  // Rounded, not truncated: 11.76" printed as 11" is a second of arc wrong on
  // every plaque, and the carry has to be handled or 59.6" becomes 60".
  let seconds = Math.round((abs - degrees - minutes / 60) * 3600);
  if (seconds >= 60) {
    seconds -= 60;
    minutes += 1;
  }
  if (minutes >= 60) {
    minutes -= 60;
    degrees += 1;
  }
  return `${degrees}°${minutes}'${seconds}"${hemisphere}`;
}

/**
 * The coordinates as a text layer prints them.
 *
 * `decimal` → `44.7866, 20.4489`; `dms` → `44°47'12"N 20°26'56"E`. Four decimals
 * for the decimal form because that is ~11 m — the precision a "where we met"
 * poster is honest about, and what Customily prints.
 */
export function formatCoordinates(
  lat: number,
  lng: number,
  format: CoordinateFormat = 'decimal',
): string {
  const safeLat = Number.isFinite(lat) ? lat : 0;
  const safeLng = Number.isFinite(lng) ? lng : 0;
  if (format === 'dms') {
    return `${dms(safeLat, 'N', 'S')} ${dms(safeLng, 'E', 'W')}`;
  }
  return `${safeLat.toFixed(4)}, ${safeLng.toFixed(4)}`;
}

/**
 * What a text layer linked to a map should say, or null when it is not linked
 * (or its map has been deleted, which must leave the line alone rather than
 * blanking it).
 *
 * Read from the map's CURRENT location, so it is right in three situations that
 * are easy to get wrong separately: before the shopper answers (the author's
 * place), after they search (their place), and in the editor (whatever the
 * merchant last dragged to). One function, called from the server's resolver and
 * the widget's live preview, so the product page and the print file cannot show
 * two different pairs of numbers under the same map.
 */
export function mapCoordinateText(
  elements: Iterable<SceneElement>,
  text: TextElement,
): string | null {
  const link = text.mapCoordinates;
  if (!link || !link.mapElementId) return null;
  for (const el of elements) {
    if (el.id !== link.mapElementId || el.type !== 'map') continue;
    const { lat, lng } = mapLocationOf(el);
    return formatCoordinates(lat, lng, link.format === 'dms' ? 'dms' : 'decimal');
  }
  return null;
}

/**
 * The style catalogue's keys and labels, in the order the picker shows them —
 * Customily's own order, read off its "Map Styles" grid.
 *
 * The KEYS and LABELS live here, in the scene package, while the Mapbox style
 * each one fetches from lives in the renderer. That split is deliberate: the
 * server needs to know a style exists (to install its thumbnail, to validate a
 * swatch) without pulling in CanvasKit, and duplicating the list in two packages
 * is how a style ends up offered in the studio with no thumbnail on the server.
 *
 * "Monochrome - Red" and "Monochrome - Black" are absent on purpose: their
 * Mapbox styles are not published yet (see `apps/migrate/src/mapbox-styles.ts`),
 * and a card in the grid that draws nothing is worse than no card.
 */
export const MAP_STYLE_CATALOGUE: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'streets', label: 'Streets' },
  { key: 'satellite', label: 'Satellite' },
  { key: 'light', label: 'Light' },
  { key: 'dark', label: 'Dark' },
  { key: 'romantic', label: 'Romantic' },
  { key: 'vintage', label: 'Vintage' },
  { key: 'noir', label: 'Noir' },
  { key: 'modern', label: 'Modern' },
  { key: 'pop-art', label: 'Pop Art' },
  { key: 'blueprint', label: 'Blueprint' },
  { key: 'sand', label: 'Sand' },
  { key: 'monochrome-green', label: 'Monochrome - Green' },
  { key: 'monochrome-red', label: 'Monochrome - Red' },
  { key: 'monochrome-blue', label: 'Monochrome - Blue' },
  { key: 'monochrome-dark-blue', label: 'Monochrome - Dark Blue' },
  { key: 'monochrome-light-blue', label: 'Monochrome - Light Blue' },
  { key: 'monochrome-white', label: 'Monochrome - White' },
  { key: 'monochrome-black', label: 'Monochrome - Black' },
];

/** Slugs a shopper-facing style label into a catalogue key. */
export function mapStyleKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** The catalogue label for a key or label, or undefined when it is neither. */
export function mapStyleLabel(name: string | undefined): string | undefined {
  if (!name) return undefined;
  const key = mapStyleKey(name);
  return MAP_STYLE_CATALOGUE.find((s) => s.key === key)?.label;
}

/**
 * The labels a picker should offer, in catalogue order, with anything we do not
 * recognise kept at the end rather than dropped.
 *
 * A style we cannot name is still a style the merchant chose — a custom Mapbox
 * URL, or one of Customily's own ids that never got a translation — and silently
 * removing it from the list removes the shopper's ability to pick what the
 * product page has been offering.
 */
export function orderMapStyles(labels: readonly string[]): string[] {
  const known: string[] = [];
  const rest: string[] = [];
  const seen = new Set<string>();
  for (const entry of MAP_STYLE_CATALOGUE) {
    if (labels.some((l) => mapStyleKey(String(l)) === entry.key)) {
      known.push(entry.label);
      seen.add(entry.key);
    }
  }
  for (const label of labels) {
    const key = mapStyleKey(String(label));
    if (seen.has(key)) continue;
    seen.add(key);
    rest.push(String(label));
  }
  return [...known, ...rest];
}
